import React from 'react';
import { Lock, ShieldAlert } from 'lucide-react';
import { Pill } from '../../components/ui';
import {
  CONFIDENTIALITY_BLURB,
  CONFIDENTIALITY_LABELS,
  Confidentiality,
  ConfidentialRecord,
  levelOf,
} from './types';

const TONE: Record<Exclude<Confidentiality, 'normal'>, 'amber' | 'red'> = {
  restricted: 'amber',
  secret: 'red',
};

/**
 * The marker that appears wherever a held-back record is listed.
 *
 * Its whole job is to make it obvious to the people who *can* see a record
 * that other people cannot — so it renders nothing at all for `normal`.
 */
export const LockMarker: React.FC<{
  record?: ConfidentialRecord | null;
  /** Pass a level directly when the record shape is not to hand */
  level?: Confidentiality;
  /** `icon` for dense rows, `pill` for headers and detail pages */
  variant?: 'icon' | 'pill';
  className?: string;
}> = ({ record, level, variant = 'icon', className = '' }) => {
  const value = level ?? levelOf(record);
  if (value === 'normal') return null;

  const label = CONFIDENTIALITY_LABELS[value];
  const title = `${label} — ${CONFIDENTIALITY_BLURB[value]}`;
  const Icon = value === 'secret' ? ShieldAlert : Lock;

  if (variant === 'pill') {
    return (
      <Pill tone={TONE[value]} title={title} className={className}>
        <Icon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" />
        {label}
      </Pill>
    );
  }

  return (
    <span
      title={title}
      aria-label={title}
      role="img"
      className={`inline-flex flex-shrink-0 items-center ${
        value === 'secret' ? 'text-primary-600' : 'text-amber-600'
      } ${className}`}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
};

export default LockMarker;
